import * as React from 'react'
import { Badge } from './Badge'
import { Focusable } from './Focusable'
import { HeroButton } from './HeroButton'
import { topicPath } from '../topic'
import type { MediaType } from '../api-types'
import type { FavoritesButtonProps, LinkComponent } from '../types'

/**
 * The content of a feature banner — the shape browse and news each map from the
 * `hero` topic (see `createHeroData`) and hand to {@link Hero}. Shared so the two
 * pilets fill the one banner component from an identical contract; {@link HeroProps}
 * extends it with the runtime dependencies the shell injects.
 */
export interface HeroData {
  /** Full-bleed background image behind the hero. */
  backgroundUrl: string;
  /**
   * The title rendered as an image (the topic's `visual_title` lockup). When the
   * editor supplied one it stands in for the textual title, which then becomes the
   * image's `alt`/`title`; when absent the banner sets the title in text instead.
   */
  logoUrl?: string;
  /**
   * The lockup's intrinsic pixel size, as the content API reports it on
   * `visual_title`. Rendered as the image's `width`/`height` attributes so the
   * browser can reserve its box from the aspect ratio before the bytes arrive —
   * without them the lockup is a zero-height image that pushes the banner's copy
   * and actions down the moment it decodes, inside the element that is usually the
   * page's LCP.
   */
  logoWidth?: number;
  logoHeight?: number;
  /** The title — shown as text, or as the `alt` of {@link HeroData.logoUrl}. */
  title: string;
  /**
   * Optional editorial standfirst (the topic's `subtitle`), set between the title
   * and the copy in a larger, bolder face than the copy itself.
   */
  subtitle?: string;
  /** Supporting copy shown under the title. */
  text: string;
  /**
   * The featured title's id (the topic's first related movie). Absent when the topic
   * has no related title — the banner then shows copy only, with no actions.
   */
  movieId?: string;
  media_type?: MediaType;
  /**
   * The id of the topic the banner was built from. When present the title links
   * through to that topic's own page, and the banner can carry a "read more" action
   * to the same place (see {@link HeroProps.topicLinkLabel}).
   */
  topicId?: string;
}

/**
 * What the banner's actions need from the host, which the styleguide cannot supply
 * itself: the shell's list toggle, and the router's link.
 */
export interface HeroActions {
  /** Favorite/watch-later toggle contributed by the shell. */
  FavoritesButton: React.FC<FavoritesButtonProps>;
  /** In-app link component (e.g. React Router's `Link`) for the banner's actions. */
  LinkComponent: LinkComponent;
  /**
   * Label for the action linking to the topic's page (e.g. "More info"). The
   * styleguide ships no copy, so the action appears only when the pilet supplies a
   * translated label — and only for a banner that carries a
   * {@link HeroData.topicId}.
   */
  topicLinkLabel?: string;
}

/**
 * The other half of the choice: a banner rendered for its visual alone.
 *
 * Stated as "none of them" rather than "each of them optional" so that a caller
 * that *means* to have actions cannot end up without them by forgetting one
 * injection — half a set is a type error, not a banner that silently loses its
 * buttons.
 */
interface HeroWithoutActions {
  FavoritesButton?: never;
  LinkComponent?: never;
  topicLinkLabel?: never;
}

export type HeroProps = HeroData & {
  /** Optional flag rendered as a {@link Badge} beside the title (e.g. `"New"`). */
  badge?: React.ReactNode;
} & (HeroActions | HeroWithoutActions)

/**
 * The full-bleed feature banner shown at the top of a browse/news view: a
 * background image, title logo, copy and the primary actions (Watch Now + the
 * favorites toggle). Shared across every pilet so the treatment stays identical.
 *
 * Injecting no {@link HeroActions} renders the banner without any of them — the
 * same composition, copy only. That is how the topic page dresses its own header:
 * it is already the destination the actions would point at.
 */
export const Hero: React.FC<HeroProps> = props => {
  const { movieId, media_type: mediaType, topicId, topicLinkLabel, FavoritesButton, LinkComponent } = props
  // Read into locals so TypeScript narrows them for the actions below: without a
  // featured title there is nothing to play and nothing to add to a list, and
  // without the host's injections there is nothing to render an action with.
  const featured = movieId != null && mediaType != null && LinkComponent != null && FavoritesButton != null
  // The banner's other destination: the topic behind it. Independent of `featured`
  // — a topic with no related title still has a page worth reading.
  const readMore = topicId != null && topicLinkLabel != null && LinkComponent != null
  // The title leads to the topic behind the banner — except where the banner *is*
  // the topic's own header, which has no host link to lead anywhere with.
  const titleLinked = topicId != null && LinkComponent != null

  // The lockup, when the topic has one, IS the title — so it carries the title as
  // its accessible name instead of the text being repeated.
  const titleContent = props.logoUrl
    ? (
      <img
        className='mlsg-hero__logo'
        src={props.logoUrl}
        alt={props.title}
        title={props.title}
        // Present whenever the API reported them; the CSS keeps the rendered size
        // (`max-width` + `height: auto`), so these only supply the aspect ratio the
        // browser reserves the box from.
        width={props.logoWidth}
        height={props.logoHeight}
      />
      )
    : props.title

  return (
    <div id='hero' className='mlsg-hero'>
      {/* The artwork is a real <img>, not a CSS `background-image`, because this is
          the page's LCP element on every browse and news view.

          A background-image is only discovered once the element it belongs to has
          been matched to a style and laid out — one whole pass after the markup
          exists — and it is fetched at the browser's default "low" priority with no
          way to raise it. An <img> is discovered as the element parses, and
          `fetchpriority='high'` puts it ahead of the rest of the page's requests,
          which is the only lever this component has over an LCP that is otherwise
          dominated by the chain that produced the URL (see {@link HeroData}).

          `aria-hidden` + an empty `alt`: it is the backdrop, not content. The
          banner's accessible name comes from its title (or the lockup's `alt`). */}
      {/* Only when there is actually artwork. `backgroundUrl` is `''` for a topic
          with no image (see `createHeroData`), and an <img> with an empty `src`
          is not a no-op the way an empty `background-image` was — the browser
          resolves it against the document and re-requests the page. */}
      {props.backgroundUrl
        ? (
          <img
            className='mlsg-hero__background'
            src={props.backgroundUrl}
            alt=''
            aria-hidden='true'
            // Camel-case here, lowercase in the DOM: React 18.3 knows this prop
            // and emits it as the `fetchpriority` attribute the browser reads.
            fetchPriority='high'
            decoding='async'
          />
          )
        : null}
      <div className='mlsg-hero__content'>
        <h2 className='mlsg-hero__title'>
          {titleLinked
            ? (
              // The title (or its lockup) leads to the topic behind the banner. Its
              // own stop, so a remote reaches it before the actions below.
              <Focusable className='mlsg-focusable--inline'>
                <LinkComponent to={topicPath(topicId)} className='mlsg-hero__title-link'>
                  {titleContent}
                </LinkComponent>
              </Focusable>
              )
            : titleContent}
          {props.badge != null ? <> <Badge>{props.badge}</Badge></> : null}
        </h2>
        {props.subtitle ? <p className='mlsg-hero__subtitle'>{props.subtitle}</p> : null}
        <p>{props.text}</p>
        {/* Each action is its own stop, so a remote can reach the hero on the way
            down to the rows instead of the rows being the only navigable region. */}
        {featured || readMore
          ? (
            <div className='mlsg-hero__actions'>
              {featured
                ? (
                  <>
                    <Focusable className='mlsg-focusable--inline'>
                      <HeroButton primary text='Watch Now' href={`/watch/${mediaType}/${movieId}`} LinkComponent={LinkComponent} />
                    </Focusable>
                    {/* NOT wrapped: this is an extension slot that renders one button per
                        registered list (favorites, watch-later). Each ListToggle registers
                        itself as its own stop — see createListToggle. */}
                    <FavoritesButton full movieId={movieId} media_type={mediaType} />
                  </>
                  )
                : null}
              {readMore
                ? (
                  <Focusable className='mlsg-focusable--inline'>
                    {/* Secondary to "Watch Now": the topic's page is the fuller read,
                        not the thing the viewer came to press. */}
                    <HeroButton primary={false} text={topicLinkLabel} href={topicPath(topicId)} LinkComponent={LinkComponent} />
                  </Focusable>
                  )
                : null}
            </div>
            )
          : null}
      </div>
      <div className='mlsg-hero__overlay' />
    </div>
  )
}
